import { DevConfig } from '../../config/environment/devConfig';
import { GAME_CONSTANTS } from '../constants/GameConstants';
import { Character } from '../../shared/CharacterTypes';
import { gameLogger } from '../../utils/GameLogger';
import { LevelStorageService } from './LevelStorageService';
import { bootSearchParams } from '../../config/boot/bootUrl';

/**
 * BootService
 * Centralizes the logic for determining how the game should start.
 * Decides the initial Map, Player State, and System Flags.
 */
export class BootService {
  private static instance: BootService;

  private constructor() { }

  public static getInstance(): BootService {
    if (!BootService.instance) {
      BootService.instance = new BootService();
    }
    return BootService.instance;
  }

  /**
   * Determine the initial Level ID to load
   * Priority:
   * 1. URL Parameter (?map=pig_beach)
   * 2. Dev Config (if in development and forceDefaultMap is true)
   * 3. Dev Map (last saved editor work - only in dev mode)
   * 4. Saved Character Position (if available)
   * 5. Hardcoded Fallback ('starter-map')
   */
  public getInitialLevelId(character?: Character | null): string {
    // 1. Check URL Params
    if (typeof window !== 'undefined') {
      const params = bootSearchParams();
      const urlMap = params.get('map');
      if (urlMap) {
        gameLogger.info('boot', `🥾 [BootService] Using Map from URL: ${urlMap}`);
        return urlMap;
      }
    }

    // 2. Check Dev Config (only if forceDefaultMap is enabled)
    // When forceDefaultMap is true, always use the configured default map
    // This is useful for testing specific maps
    if (DevConfig.forceDefaultMap && DevConfig.defaultMap) {
      gameLogger.info('boot', `🥾 [BootService] Using DevConfig Map (forced): ${DevConfig.defaultMap}`);
      return DevConfig.defaultMap;
    }

    // 3. Check for dev-map (developer's last saved work)
    // This allows the editor "Save Dev" feature to persist across reloads
    if (typeof window !== 'undefined') {
      const levelStorage = LevelStorageService.getInstance();
      const devMap = levelStorage.getDevMap();
      if (devMap) {
        gameLogger.info('boot', '🥾 [BootService] Using Dev Map (last saved editor work): dev-map');
        return 'dev-map';
      }
    }

    // 4. Check Dev Config (fallback when not forced)
    // If no dev-map exists, use the default map from config
    if (DevConfig.defaultMap) {
      gameLogger.info('boot', `🥾 [BootService] Using DevConfig Map: ${DevConfig.defaultMap}`);
      return DevConfig.defaultMap;
    }

    // 5. Saved Character Position
    if (character && (character as any).currentLevelId) {
      const savedLevel = (character as any).currentLevelId;
      gameLogger.info('boot', `🥾 [BootService] Resuming at saved level: ${savedLevel}`);
      return savedLevel;
    }

    // 6. Default Fallback
    const fallbackMap = GAME_CONSTANTS.DEFAULTS.MAP;
    gameLogger.info('boot', `🥾 [BootService] Using Fallback Map: ${fallbackMap}`);
    return fallbackMap;
  }

  /**
   * Resolve an optional named arrival anchor from the URL. Cross-map
   * teleporters use the destination teleporter id so the player enters a map
   * from the side they actually travelled from instead of its generic spawn.
   */
  public getInitialSpawnId(): string | undefined {
    if (typeof window === 'undefined') return undefined;

    const spawnId = bootSearchParams().get('spawn');
    return spawnId?.trim() || undefined;
  }

  /** Opaque server-issued receipt carried only across one full-page transfer. */
  public getWorldTransferReceipt(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    const receipt = bootSearchParams().get('transfer');
    return receipt?.trim() || undefined;
  }

  /**
   * validate connection or other startup checks
   */
  public async preFlightCheck(): Promise<boolean> {
    // Potential connectivity checks here
    return true;
  }
}
